import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Game } from "./engine/Game";
import { ChallengeRouter } from "./ui/challenges/ChallengeRouter";
import { HUD } from "./ui/HUD";
import { TouchControls } from "./ui/TouchControls";
import type { GameConfig, GameEvent, RunOutcome, RunState } from "./types";
import type { WordDTO } from "@shared/types";
import { RUNNER } from "@shared/constants";
import { loadGameSettings, saveGameSettings, type GameSettings, type MovementMode } from "../local/gameSettings";
import { refreshAudioSettings, sfx } from "./audio";

interface RemotePos {
  id: string;
  s: number;
  laneOffset: number;
  state: string;
  updatedAt: number;
}

interface Props {
  config: GameConfig;
  onEnd: (outcome: RunOutcome) => void;
  /** Multiplayer: live position map keyed by player id (other players only). */
  positions?: Map<string, RemotePos>;
  /** Multiplayer: called when our own player moves (for broadcasting ticks). */
  onMyTick?: (s: number, laneOffset: number, state: string) => void;
  /** When true, the game is paused (e.g. while the 3-2-1 countdown plays). */
  paused?: boolean;
  /** Rendered as an in-world Three.js arcade countdown sign. */
  countdownLabel?: string | null;
  /** Hide the pause card during pre-start camera/countdown moments. */
  showPauseOverlay?: boolean;
}

export function GameView({
  config,
  onEnd,
  positions,
  onMyTick,
  paused,
  countdownLabel,
  showPauseOverlay = true,
}: Props) {
  const nav = useNavigate();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const gameRef = useRef<Game | null>(null);
  const onEndRef = useRef(onEnd);
  const positionsRef = useRef(positions);
  const onMyTickRef = useRef(onMyTick);
  const [state, setState] = useState<RunState>({
    hearts: RUNNER.startingHearts,
    stumbles: 0,
    coins: 0,
    score: 0,
    startedAt: performance.now(),
    finishedAt: null,
    distance: 0,
    paused: false,
    speed: RUNNER.baseSpeed,
    invulnerableUntil: 0,
  });
  const [activeChallenge, setActiveChallenge] = useState<{
    word: WordDTO;
    distractors: WordDTO[];
    resolve: (correct: boolean) => void;
  } | null>(null);
  const [floats, setFloats] = useState<{ id: number; value: number }[]>([]);
  const [showRunSettings, setShowRunSettings] = useState(false);
  const [gameSettings, setGameSettings] = useState<GameSettings>(() => loadGameSettings());

  useEffect(() => {
    onEndRef.current = onEnd;
    positionsRef.current = positions;
    onMyTickRef.current = onMyTick;
  }, [onEnd, positions, onMyTick]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleEvent = (e: GameEvent) => {
      switch (e.type) {
        case "state":
          setState(e.state);
          if (!e.state.paused) setShowRunSettings(false);
          break;
        case "coin": {
          const id = performance.now() + Math.random();
          setFloats((arr) => [...arr.slice(-12), { id, value: e.value }]);
          setTimeout(() => setFloats((arr) => arr.filter((f) => f.id !== id)), 900);
          break;
        }
        case "challenge": {
          const others = config.words.filter((w) => w.id !== e.word.id);
          const distractors = others.sort(() => Math.random() - 0.5).slice(0, 3);
          setActiveChallenge({ word: e.word, distractors, resolve: e.resolve });
          break;
        }
        case "finish":
        case "death":
          onEndRef.current(e.outcome);
          break;
      }
    };

    const game = new Game(canvas, config, handleEvent);
    gameRef.current = game;

    // Pump remote ghost positions into the game + broadcast our own.
    let mpRaf = 0;
    const pump = () => {
      mpRaf = requestAnimationFrame(pump);
      if (positionsRef.current) {
        for (const [id, p] of positionsRef.current) {
          game.setRemotePosition(id, p.s, p.laneOffset, p.state);
        }
      }
      // Broadcast our own ~12 Hz tick.
      onMyTickRef.current?.(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (game as any).player?.s ?? 0,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (game as any).player?.laneOffset ?? 0,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (game as any).player?.state ?? "run",
      );
    };
    if (positionsRef.current || onMyTickRef.current) mpRaf = requestAnimationFrame(pump);

    return () => {
      cancelAnimationFrame(mpRaf);
      game.dispose();
      gameRef.current = null;
    };
  }, [config]);

  // External pause control (countdown overlay).
  useEffect(() => {
    if (paused === undefined) return;
    gameRef.current?.setPaused(paused);
    if (!paused) setShowRunSettings(false);
  }, [paused]);

  // In-world countdown sign, not a DOM popup.
  useEffect(() => {
    gameRef.current?.setCountdownLabel(countdownLabel ?? null);
  }, [countdownLabel]);

  const handleAnswer = (correct: boolean) => {
    activeChallenge?.resolve(correct);
    setActiveChallenge(null);
  };

  const applyRunSettings = (patch: Partial<GameSettings>) => {
    const next = { ...gameSettings, ...patch };
    setGameSettings(next);
    saveGameSettings(next);
    refreshAudioSettings();
    gameRef.current?.updateSettings(next);
    if (patch.audioVolume !== undefined || patch.movementMode !== undefined) sfx.menu();
  };

  const resumeGame = () => {
    setShowRunSettings(false);
    gameRef.current?.setPaused(false);
  };

  return (
    <div className="game-root">
      <canvas ref={canvasRef} className="game-canvas" />
      <HUD
        state={state}
        totalDistance={config.courseLength}
        onPause={() => gameRef.current?.togglePause()}
      />
      {!activeChallenge && !state.paused && (
        <TouchControls
          onMove={(x, y) => gameRef.current?.setMoveVector(x, y)}
          onAction={() => gameRef.current?.jump()}
        />
      )}
      <div className="coin-floats">
        {floats.map((f) => (
          <div key={f.id} className="coin-float">
            +{f.value} 🪙
          </div>
        ))}
      </div>
      {activeChallenge && (
        <ChallengeRouter
          word={activeChallenge.word}
          distractors={activeChallenge.distractors}
          onAnswer={handleAnswer}
          rotationSeed={config.seed}
          allowedKinds={config.challengeKinds}
        />
      )}
      {showPauseOverlay && state.paused && !activeChallenge && !countdownLabel && (
        <div className="pause-overlay">
          {!showRunSettings && (
            <div className="pause-menu">
              <div className="pause-menu-title">Paused</div>
              <div className="pause-menu-actions">
                <button className="pause-btn pause-btn-resume" onClick={resumeGame}>
                  ▶ Resume
                </button>
                <button className="pause-btn pause-btn-settings" onClick={() => setShowRunSettings(true)}>
                  ⚙ Settings
                </button>
                <button className="pause-btn pause-btn-exit" onClick={() => nav("/play")}>
                  ← Exit
                </button>
              </div>
            </div>
          )}
          {showRunSettings && (
            <RunSettingsOverlay
              settings={gameSettings}
              onChange={applyRunSettings}
              onBack={() => setShowRunSettings(false)}
            />
          )}
        </div>
      )}
    </div>
  );
}

function RunSettingsOverlay({
  settings,
  onChange,
  onBack,
}: {
  settings: GameSettings;
  onChange: (patch: Partial<GameSettings>) => void;
  onBack: () => void;
}) {
  const setMovementMode = (mode: MovementMode) => onChange({ movementMode: mode });
  return (
    <div className="run-settings-panel" role="dialog" aria-label="Game settings">
      <div className="run-settings-top">
        <button className="run-settings-back" onClick={onBack} aria-label="Back to pause menu">←</button>
        <h2>Settings</h2>
      </div>
      <div className="run-settings-grid">
        <div className="run-settings-section">
          <h3>Controls</h3>
          <div className="segmented">
            <button
              className={settings.movementMode === "free" ? "active" : ""}
              onClick={() => setMovementMode("free")}
            >
              Free
            </button>
            <button
              className={settings.movementMode === "lanes" ? "active" : ""}
              onClick={() => setMovementMode("lanes")}
            >
              Lanes
            </button>
          </div>
          <RunSlider
            label="Movement sensitivity"
            value={settings.movementSensitivity}
            min={0.25}
            max={1.5}
            onChange={(movementSensitivity) => onChange({ movementSensitivity })}
          />
          <RunSlider
            label="Controller deadzone"
            value={settings.controllerDeadzone}
            min={0.05}
            max={0.4}
            onChange={(controllerDeadzone) => onChange({ controllerDeadzone })}
          />
          <RunSlider
            label="Camera sensitivity"
            value={settings.cameraSensitivity}
            min={0.25}
            max={1.5}
            onChange={(cameraSensitivity) => onChange({ cameraSensitivity })}
          />
        </div>
        <div className="run-settings-section">
          <h3>Sound</h3>
          <RunSlider
            label="Voice and SFX"
            value={settings.audioVolume}
            min={0}
            max={1}
            onChange={(audioVolume) => onChange({ audioVolume })}
          />
          <RunSlider
            label="Menu music"
            value={settings.musicVolume}
            min={0}
            max={1}
            onChange={(musicVolume) => onChange({ musicVolume })}
          />
        </div>
      </div>
    </div>
  );
}

function RunSlider({
  label,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
}) {
  return (
    <label className="settings-slider run-settings-slider">
      <span>{label}</span>
      <strong>{Math.round(value * 100)}%</strong>
      <input
        type="range"
        min={Math.round(min * 100)}
        max={Math.round(max * 100)}
        value={Math.round(value * 100)}
        onChange={(e) => onChange(Number(e.target.value) / 100)}
      />
    </label>
  );
}
